import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getReferralCode } from '../utils/referral';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import ScheduleSessionPage from './ScheduleSessionPage';
import TestimonialCarousel from '../components/TestimonialCarousel';
import {
  Sparkles, Check, ArrowRight, Clock, Briefcase, CalendarCheck, ShieldCheck,
  Award, Target, ChevronRight, Quote,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Map each dedicated URL slug → page config.
 * - kind: 'free' renders embedded /schedule flow.
 * - kind: 'paid_session' wires Stripe checkout via /api/payments/checkout.
 * - kind: 'application' routes to /program-application?program=<application_slug>.
 */
export const PROGRAM_PAGE_CONFIG = {
  'free-jumpstart-session': {
    badge: 'Start Here',
    title: 'FREE Business Jumpstart Session',
    highlightWord: 'FREE',
    price: 'FREE',
    duration: '20 Minutes',
    description: 'A free introductory clarity session designed to help aspiring entrepreneurs identify next steps, overcome obstacles, and gain direction for launching or growing their business.',
    features: [
      'Business idea discussion',
      'Startup direction',
      'Goal clarity',
      'Q&A session',
      'Personalized recommendations',
    ],
    kind: 'free',
    accent: 'hero',
    ctaText: 'Pick a Date Below',
    seo: {
      title: 'FREE Business Jumpstart Session - 20-Minute Clarity Call with David C. Ashley',
      description: 'Book a FREE 20-minute Business Jumpstart Session. Identify next steps, overcome obstacles, and gain direction for launching or growing your business.',
    },
  },
  'business-strategy-session': {
    badge: 'Most Booked',
    title: '1-on-1 Business Strategy Session',
    highlightWord: '1-on-1',
    price: '$197',
    duration: '60 Minutes',
    description: 'A personalized strategy session focused on helping entrepreneurs launch, structure, or grow their business with clear action steps and expert guidance.',
    features: [
      'Personalized business strategy',
      'Launch roadmap',
      'Business structure guidance',
      'Branding direction',
      'Credibility recommendations',
      'Action plan after session',
    ],
    kind: 'paid_session',
    programSlug: 'business-strategy-session',
    accent: 'blue',
    ctaText: 'Book Strategy Session - $197',
    seo: {
      title: '1-on-1 Business Strategy Session - 60-Minute Coaching with David C. Ashley',
      description: 'Personalized 60-minute business strategy session with David C. Ashley. $197. Get a clear roadmap, structure, branding, and growth plan.',
    },
  },
  'business-jumpstart-accelerator': {
    badge: '3-Month Program',
    title: 'Business Jumpstart Accelerator',
    highlightWord: 'Accelerator',
    price: '$997',
    payment: 'or 3 payments of $397',
    duration: '3 Months',
    description: 'An accountability-based coaching program designed to help entrepreneurs move from idea to launch with structure, consistency, and support.',
    features: [
      'Bi-weekly coaching sessions',
      'Launch strategy support',
      'Business credibility setup',
      'Branding & positioning guidance',
      'Accountability check-ins',
      'Resource vault access',
      'Email/message support',
    ],
    kind: 'application',
    applicationSlug: 'business-jumpstart-accelerator',
    accent: 'standard',
    ctaText: 'Apply Now',
    seo: {
      title: 'Business Jumpstart Accelerator - 3-Month Coaching Program | DCA Enterprises',
      description: 'A 3-month accountability coaching program to take you from idea to launch with structure and consistency.',
    },
  },
  'business-growth-partnership': {
    badge: '6-Month Program',
    title: 'Business Growth Partnership',
    highlightWord: 'Partnership',
    price: '$1,997',
    payment: 'or monthly payment option',
    duration: '6 Months',
    description: 'A long-term business development partnership focused on implementation, consistency, systems, and sustainable growth.',
    features: [
      'Ongoing coaching support',
      'Monthly growth planning',
      'Systems & organization guidance',
      'Business structure refinement',
      'Marketing & positioning support',
      'Accountability coaching',
      'Priority support access',
    ],
    kind: 'application',
    applicationSlug: 'business-growth-partnership',
    accent: 'premium',
    ctaText: 'Apply for Partnership',
    seo: {
      title: 'Business Growth Partnership - 6-Month Coaching Program | DCA Enterprises',
      description: 'A 6-month business development partnership focused on implementation, systems, and sustainable growth.',
    },
  },
  'visionary-mentorship': {
    badge: '12-Month Mentorship',
    title: 'Visionary CEO Mentorship',
    highlightWord: 'Visionary',
    price: '$4,997',
    payment: 'or monthly payment option',
    duration: '12 Months',
    description: 'A high-level mentorship experience for serious entrepreneurs committed to long-term growth, leadership, scalability, and business expansion.',
    features: [
      'Long-term strategy partnership',
      'Leadership development',
      'Business scaling guidance',
      'Multiple income stream planning',
      'Systems implementation',
      'Advanced accountability',
      'Priority consulting access',
    ],
    kind: 'application',
    applicationSlug: 'visionary-ceo-mentorship',
    accent: 'premium',
    ctaText: 'Apply for Mentorship',
    seo: {
      title: 'Visionary CEO Mentorship - 12-Month High-Level Program | DCA Enterprises',
      description: 'A 12-month mentorship for serious entrepreneurs committed to leadership, scale, and expansion. $4,997.',
    },
  },
};

function ProgramHeroCard({ cfg, processing, onPaidCheckout }) {
  const isFree = cfg.kind === 'free';
  const isPremium = cfg.accent === 'premium';
  const cardClass = isFree
    ? 'bg-gradient-to-br from-[#0F172A] to-[#1E1B4B] text-white border-2 border-[#F97316] shadow-2xl shadow-[#1D4ED8]/30'
    : isPremium
    ? 'bg-white border-2 border-[#1D4ED8]/30 shadow-xl shadow-slate-100'
    : cfg.accent === 'blue'
    ? 'bg-white border border-[#1D4ED8]/20 shadow-xl shadow-slate-100'
    : 'bg-white border border-[#E2E8F0] shadow-xl shadow-slate-100';
  const priceColor = isFree ? 'text-[#FB923C]' : isPremium ? 'text-[#1D4ED8]' : 'text-[#0A0F1C]';
  const descColor = isFree ? 'text-[#94A3B8]' : 'text-[#475569]';
  const headingColor = isFree ? 'text-white' : 'text-[#0A0F1C]';
  const checkColor = isFree ? 'text-[#FB923C]' : isPremium ? 'text-[#1D4ED8]' : 'text-[#10B981]';

  return (
    <div className={`relative rounded-2xl p-7 sm:p-10 ${cardClass}`} data-testid="program-hero-card">
      {cfg.badge && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F97316] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
          {cfg.badge}
        </span>
      )}
      <div className="mb-5">
        <h2 className={`text-2xl sm:text-3xl font-bold ${headingColor} mb-3 leading-tight`} data-testid="program-card-title">
          {cfg.title}
        </h2>
        <div className="flex items-baseline gap-2 mb-1 flex-wrap">
          <span className={`text-4xl sm:text-5xl font-bold ${priceColor}`} data-testid="program-card-price">{cfg.price}</span>
          <span className={`text-sm ${descColor}`}>· {cfg.duration}</span>
        </div>
        {cfg.payment && <div className={`text-xs ${descColor} mt-1`}>{cfg.payment}</div>}
      </div>
      <p className={`text-base ${descColor} mb-6 leading-relaxed`}>{cfg.description}</p>
      <ul className="space-y-3 mb-7">
        {cfg.features.map((f, i) => (
          <li key={i} className={`flex items-start gap-3 text-sm ${isFree ? 'text-[#E2E8F0]' : 'text-[#475569]'}`}>
            <Check size={16} className={`${checkColor} mt-0.5 flex-shrink-0`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {cfg.kind === 'free' && (
        <a href="#schedule" className="btn-primary-orange inline-flex items-center justify-center gap-2 w-full text-base sm:text-lg py-3" data-testid="hero-card-cta">
          {cfg.ctaText} <ArrowRight size={16} />
        </a>
      )}
      {cfg.kind === 'paid_session' && (
        <Button
          onClick={onPaidCheckout}
          disabled={processing}
          className="btn-primary-orange w-full text-base sm:text-lg py-3"
          data-testid="hero-card-cta"
        >
          {processing ? 'Loading...' : (<><span>{cfg.ctaText}</span><ArrowRight size={16} className="ml-2" /></>)}
        </Button>
      )}
      {cfg.kind === 'application' && (
        <Link
          to={`/program-application?program=${encodeURIComponent(cfg.applicationSlug)}`}
          className={`${isPremium ? 'btn-primary-blue' : 'btn-primary-orange'} inline-flex items-center justify-center gap-2 w-full text-base sm:text-lg py-3`}
          data-testid="hero-card-cta"
        >
          {cfg.ctaText} <ArrowRight size={16} />
        </Link>
      )}
    </div>
  );
}

export default function ProgramLandingPage({ slug }) {
  const cfg = PROGRAM_PAGE_CONFIG[slug];
  const [processing, setProcessing] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [schedulerStep, setSchedulerStep] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (cfg?.kind === 'paid_session') {
      axios.get(`${API_URL}/api/programs`).then((r) => setPrograms(r.data)).catch(() => {});
    }
    axios.get(`${API_URL}/api/testimonials`).then((r) => setTestimonials(r.data || [])).catch(() => {});
  }, [cfg]);

  if (!cfg) {
    return (
      <Layout title="Program Not Found" description="Program not found.">
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-[#0A0F1C] mb-3">Program not found</h1>
            <Link to="/programs" className="text-[#1D4ED8] hover:underline">View all programs</Link>
          </div>
        </div>
      </Layout>
    );
  }

  const handlePaidCheckout = async () => {
    const program = programs.find((p) => p.slug === cfg.programSlug);
    if (!program?.program_id) {
      toast.error('Program not available. Please contact us directly.');
      return;
    }
    setProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/payments/checkout`, {
        item_type: 'program',
        item_id: program.program_id,
        origin_url: window.location.origin,
        name: '',
        email: '',
        ref: getReferralCode(),
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setProcessing(false);
    }
  };

  return (
    <Layout title={cfg.seo.title} description={cfg.seo.description}>
      <div className="bg-[#FAFAFA] min-h-screen" data-testid="program-landing-page">
        {/* HERO + CARD */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] text-white py-14 sm:py-20">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
          <div className="absolute -top-10 -left-10 w-80 h-80 rounded-full bg-[#1D4ED8] opacity-30 blur-3xl"></div>
          <div className="absolute -bottom-10 -right-10 w-80 h-80 rounded-full bg-[#F97316] opacity-20 blur-3xl"></div>

          <div className="container-custom relative z-10 max-w-3xl mx-auto text-center">
            <Link to="/programs" className="text-xs text-[#94A3B8] hover:text-white inline-flex items-center gap-1 mb-3" data-testid="back-to-programs">
              <ArrowRight size={12} className="rotate-180" /> Back to All Programs
            </Link>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-xs sm:text-sm font-semibold rounded-full mb-4" data-testid="program-tagline-badge">
              <Sparkles size={14} className="text-[#FB923C]" /> {cfg.duration} · {cfg.price}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight" data-testid="program-landing-title">
              {cfg.title.split(cfg.highlightWord).map((part, i, arr) => (
                <React.Fragment key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <span className="bg-gradient-to-r from-[#60A5FA] to-[#F97316] bg-clip-text text-transparent">{cfg.highlightWord}</span>
                  )}
                </React.Fragment>
              ))}
            </h1>
            <p className="text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-2xl mx-auto">
              {cfg.description}
            </p>
          </div>
        </section>

        {/* CARD SECTION - hidden for the free flow once the user advances past date selection */}
        {(cfg.kind !== 'free' || schedulerStep === 1) && (
          <section className="container-custom max-w-3xl py-12 sm:py-16 -mt-8 sm:-mt-12 relative z-20">
            <ProgramHeroCard cfg={cfg} processing={processing} onPaidCheckout={handlePaidCheckout} />
          </section>
        )}

        {/* FREE: EMBEDDED SCHEDULER */}
        {cfg.kind === 'free' && (
          <section id="schedule" className="bg-white border-t border-[#E2E8F0]" data-testid="embedded-schedule-section">
            {schedulerStep === 1 && (
              <div className="container-custom max-w-4xl text-center pt-12 sm:pt-16 pb-2" data-testid="schedule-intro-header">
                <div className="text-xs font-bold text-[#1D4ED8] uppercase tracking-wider mb-2">Step 2 of 2</div>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0F1C] mb-3">Pick Your Date &amp; Time</h2>
                <p className="text-[#475569] mb-2">Closed Sundays &amp; 2nd Saturdays. Sessions are held over Zoom.</p>
              </div>
            )}
            <ScheduleSessionPage embed={true} onStepChange={setSchedulerStep} />
          </section>
        )}

        {/* PAID: TRUST SIGNALS */}
        {cfg.kind !== 'free' && (
          <section className="container-custom max-w-4xl py-12 sm:py-16" data-testid="trust-signals-section">
            <div className="grid sm:grid-cols-3 gap-5">
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 text-center">
                <Award size={24} className="text-[#1D4ED8] mx-auto mb-2" />
                <div className="font-bold text-[#0A0F1C] text-sm mb-1">Direct Access to David</div>
                <div className="text-xs text-[#475569]">No assistants. Just you and David working through your business.</div>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 text-center">
                <Target size={24} className="text-[#1D4ED8] mx-auto mb-2" />
                <div className="font-bold text-[#0A0F1C] text-sm mb-1">Action-First Coaching</div>
                <div className="text-xs text-[#475569]">Every session ends with concrete next steps you can execute.</div>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 text-center">
                <ShieldCheck size={24} className="text-[#1D4ED8] mx-auto mb-2" />
                <div className="font-bold text-[#0A0F1C] text-sm mb-1">Application Reviewed Personally</div>
                <div className="text-xs text-[#475569]">David reads every application and replies within 48 hours.</div>
              </div>
            </div>
          </section>
        )}

        {/* NEXT STEPS */}
        <section className="bg-white py-12 sm:py-16" data-testid="next-steps-section">
          <div className="container-custom max-w-3xl">
            <div className="text-center mb-8">
              <div className="text-xs font-bold text-[#1D4ED8] uppercase tracking-wider mb-2">What Happens Next</div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0F1C]">Simple, Clear, Fast</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              {cfg.kind === 'free' && (
                <>
                  <NextStep n={1} title="Pick your date" desc="Choose a slot that works with your schedule." />
                  <NextStep n={2} title="Share your vision" desc="Answer the quick 6-question intake so David comes prepared." />
                  <NextStep n={3} title="Hop on Zoom" desc="20 minutes with David. Walk away with clarity." />
                </>
              )}
              {cfg.kind === 'paid_session' && (
                <>
                  <NextStep n={1} title="Checkout securely" desc="Pay $197 via Stripe (Apple Pay, Google Pay, card)." />
                  <NextStep n={2} title="Receive intake link" desc="Complete the prep questions David will review." />
                  <NextStep n={3} title="60 minutes 1-on-1" desc="Get your personalized strategy and roadmap." />
                </>
              )}
              {cfg.kind === 'application' && (
                <>
                  <NextStep n={1} title="Submit your application" desc="Tell David about your business, goals, and timeline." />
                  <NextStep n={2} title="Discovery call" desc="If aligned, David invites you to a no-pressure fit call." />
                  <NextStep n={3} title="Onboard &amp; begin" desc="Receive your roadmap, payment plan, and first session." />
                </>
              )}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        {testimonials.length > 0 ? (
          <section className="py-8" data-testid="program-testimonials">
            <div className="text-center mb-6 container-custom">
              <div className="text-xs font-bold uppercase tracking-wider text-[#FB923C] mb-2">Real Stories</div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0F1C]">Client Success Stories</h2>
            </div>
            <TestimonialCarousel testimonials={testimonials} bgGradientFrom="#FAFAFA" />
          </section>
        ) : (
          <section className="container-custom max-w-3xl py-12 sm:py-16">
            <div className="bg-gradient-to-br from-[#F8FAFC] to-white border border-dashed border-[#E2E8F0] rounded-2xl p-10 text-center" data-testid="testimonial-placeholder">
              <Quote size={32} className="text-[#CBD5E1] mx-auto mb-4" />
              <h3 className="font-bold text-[#0A0F1C] text-lg mb-2">Client success stories coming soon</h3>
              <p className="text-sm text-[#475569] max-w-md mx-auto">Real entrepreneur transformations being documented now.</p>
            </div>
          </section>
        )}

        {/* FINAL CTA */}
        <section className="bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] text-white py-14 sm:py-20" data-testid="program-final-cta">
          <div className="container-custom text-center max-w-2xl mx-auto">
            <CalendarCheck size={40} className="text-[#FB923C] mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 leading-tight">Ready to take the next step?</h2>
            <p className="text-sm sm:text-base text-[#94A3B8] mb-6">{cfg.description}</p>
            {cfg.kind === 'free' && (
              <a href="#schedule" className="btn-primary-orange inline-flex items-center gap-2 text-base px-7 py-3" data-testid="final-cta-btn">
                Pick a Date Above <ArrowRight size={16} />
              </a>
            )}
            {cfg.kind === 'paid_session' && (
              <Button onClick={handlePaidCheckout} disabled={processing} className="btn-primary-orange inline-flex items-center gap-2 text-base px-7 py-3" data-testid="final-cta-btn">
                {processing ? 'Loading...' : (<>{cfg.ctaText} <ArrowRight size={16} className="ml-2" /></>)}
              </Button>
            )}
            {cfg.kind === 'application' && (
              <Link to={`/program-application?program=${encodeURIComponent(cfg.applicationSlug)}`} className="btn-primary-orange inline-flex items-center gap-2 text-base px-7 py-3" data-testid="final-cta-btn">
                {cfg.ctaText} <ArrowRight size={16} />
              </Link>
            )}
            <div className="mt-6 text-xs text-[#94A3B8]">
              Not the right fit? <Link to="/programs" className="text-[#60A5FA] hover:underline">Compare all programs <ChevronRight size={12} className="inline" /></Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

function NextStep({ n, title, desc }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 relative" data-testid={`next-step-${n}`}>
      <div className="absolute -top-3 left-5 w-7 h-7 rounded-full bg-[#1D4ED8] text-white flex items-center justify-center text-xs font-bold">{n}</div>
      <h3 className="font-bold text-[#0A0F1C] text-base mb-1 mt-2">{title}</h3>
      <p className="text-sm text-[#475569] leading-relaxed">{desc}</p>
    </div>
  );
}
